import React from 'react';
import { GridComponent } from 'bkt-react';
import { mapValues, isEmpty, orderBy } from 'lodash';
import { Empty, GridContainer } from './styles';
import { injectCustomUI, emphasizeTimeForDateTime, showCellWithSimilarHighlight, showScenarioAsLink } from './BktGridElemInject';
import {
  TYPES, TIME_FORMAT, DATE_FORMAT, DATETIME_FORMAT,
} from '../../constants/grid.constants';
import { camelToSnake, snakeToCamel } from '../../helpers';
import LinkIcon from '../DataPage/LinkIcon';

const show = (...x) => (x);
const wrapLookups = lookups => mapValues(lookups, o => ({ data: orderBy(o, [c => c.name], ['asc']) }));
const withDataLinks = rows => rows.map(row => ({ ...row, linked: (row.id === row.itemId) }));
const formatData = ({
  config, meta, data, addLinkColumn, onSelect, checkedState, onNested, onGanttData,
  onInfo, lookups, useSimilarMatches, gantt,
}) => {
  const rows = addLinkColumn
    ? withDataLinks(data)
    : data;

  // If the backend is sending metadata - applies to the data page and scenario compare.
  const columnsFromMeta = ({ columns }) => (
    columns || []
  ).map((column) => {
    const obj = {
      key: column.name,
      name: column.display_name,
      inputType: column.type,
      editable: column.editable,
      filterable: config.includeFilter,
      sortable: config.includeSort,
      nullable: !column.required,
    };

    const {
      type, lookup, lookup_type, name, display_name, enable_link, has_delta
    } = column;

    if (type === TYPES.lookup
      && lookups
      && lookups[snakeToCamel(lookup_type)]
    ) {
      obj.lookup = {
        dataSource: snakeToCamel(lookup_type),
        displayField: 'name',
      };
    }

    // Detect custom column for adding extra functionality for BktGrid.
    // TODO: Currently the injection done in data page can only be done correctly
    // within __FUNC_COLUMN. The future is to find solution to break this restraints.
    if (name === '__FUNC_COLUMN') {
      obj.width = 40;
      obj.filterable = false;
      obj.editable = false;
      obj.nullable = false;
      obj.cellRenderer = cellRendProps => (
        injectCustomUI(cellRendProps, rows[cellRendProps.rowIndex], onSelect, checkedState)
      );
    }

    if (type === TYPES.date) {
      obj.format = DATE_FORMAT;
      obj.sendDataUseDisplayFormat = true;
      obj.alternativeFilter = 'basic';
    }

    if (type === TYPES.duration) {
      obj.inputType = 'durationInHM';
    }

    if (type === TYPES.time) {
      obj.format = TIME_FORMAT;
    }

    if (type === TYPES.datetime) {
      obj.format = DATETIME_FORMAT;
      obj.sendDataUseDisplayFormat = true;
      obj.filterFormat = DATE_FORMAT;
      obj.alternativeFilter = 'basic';

      if (meta && meta.emphasize_time_for_date_time) {
        obj.cellRenderer = emphasizeTimeForDateTime;
      }
    }

    // The below object manipulation does not have immutablitiy issue due to the fact that you are
    // still in the process of creating a new object, as oppose to changing an existing object.
    if (type === TYPES.child) {
      obj.editable = false;
      obj.filterable = false;
      obj.sortable = false;
      obj.cellRenderer = ({ cellData, rowIndex, dataKey }) => onNested(display_name, {
        type: camelToSnake(lookup),
        name,
        row: data[rowIndex],
        content: cellData,
        useSimilarMatches: ( // This is only used in ScenarioCompareNested.
          useSimilarMatches && useSimilarMatches[rowIndex].find(col => dataKey.match(col))
        ), // match is used in here because backend is throwing a plural and a singular variable
      });
    }

    // TODO: Will need to change the TYPES because Shift Summary data is not in JSON.
    // There used to be onJSON prop but is tied up with gantt and this is not good in general.
    // We cannot just make a list type as well because this is really gantt specific thing.
    // Please do not mix-use-case with general prop with specfic type of function.
    if (type === TYPES.json) {
      obj.cellRenderer = ({ cellData, rowData }) => isEmpty(cellData) ? ''
        : onGanttData(cellData, rowData, gantt);
    }

    // The below are specifically made for scenario compare.
    if (useSimilarMatches && type !== TYPES.child) {
      obj.cellRenderer = cellRendProps => showCellWithSimilarHighlight(
        cellRendProps, useSimilarMatches,
      );
    }

    if (useSimilarMatches && type === TYPES.info) {
      obj.cellRenderer = ({ cellData }) => onInfo(cellData);
    }

    if (has_delta) {
      obj.cellRenderer = cellRendProps => (
        injectCustomUI(cellRendProps, rows[cellRendProps.rowIndex], onSelect, checkedState)
      );
    }

    // This is used in scenario page to generate links to open modal.
    if (enable_link) {
      obj.cellRenderer = cellRendProps => showScenarioAsLink(cellRendProps, onSelect, column);
    }

    return obj;
  });

  // If backend is not sending meta - normally this is attached to scenario page.
  const columnsFromData = columns => columns
    .map((column) => {
      const obj = {
        key: column,
        name: column,
        filterable: false,
        ...((column === '') ? { width: 40 } : {}),
      };

      // Detect custom column for adding extra functionality for BktGrid.
      if (column === '__FUNC_COLUMN') {
        obj.name = '';
        obj.width = 40;
        obj.filterable = false;
        obj.editable = false;
        obj.nullable = false;
      }

      obj.cellRenderer = cellRendProps => (
        injectCustomUI(cellRendProps, rows[cellRendProps.rowIndex], onSelect, checkedState)
      );

      return obj;
    });

  // Yields an object that describes how bkt-grid columns should be rendered.
  // Can be constructed from either:
  //   - A meta object provided from the backend. (Needed for the Data Page)
  //   - The data in the first row of the grid. (How all other grids are construted)
  //
  // meta.currentMeta is used for data page, and meta itself is for data pages' modal.
  const formattedColumns = meta
    ? columnsFromMeta(meta)
    : columnsFromData(Object.keys({ ...rows[0] }));

  const linkColumn = {
    key: 'linkStatus',
    name: 'Link Status',
    inputType: 'string',
    filterable: false,
    width: 55,
    editable: false,
    cellRenderer: ({ cellData }) => <LinkIcon type={cellData} />,
  };

  const columns = [
    ...formattedColumns,
    ...(addLinkColumn ? [linkColumn] : []),
  ];

  return ({ columns, rows });
};

const defaultApi = {
  get: show,
  put: show,
  post: show,
  delete: show,
};


const grid = (
  { columns, rows },
  api,
  gridConfig,
  noDataMessage,
  height,
  lookups,
  className,
  updateConfig,
  isNotDeletable,
) => {
  const data = rows.results
    ? { ...rows, results: rows.results }
    : rows;

  const noData = (data.results ? !data.results[0] : !data[0]);

  return <>
    <GridContainer
      className={className}
      height={height || '100%'}
      isNotDeletable={isNotDeletable}
    >
      {noData && <Empty>{noDataMessage}</Empty>}
      <GridComponent
        columns={columns}
        data={!noData && data}
        config={gridConfig}
        updateConfig={updateConfig}
        dependencyData={wrapLookups(lookups)}
        api={api || defaultApi}
      />
    </GridContainer>
  </>;
};

const UseBktGrid = ({
  meta,
  lookups,
  data,
  config,
  addLinkColumn,
  onNested,
  onGanttData,
  onInfo,
  useSimilarMatches,
  api,
  onSelect,
  checkedState,
  noDataMessage,
  height,
  className,
  viewOnly,
  updateConfig,
  isNotDeletable,
  gantt,
}) => {
  const modConfigBeforeFormatting = { ...config };

  if (meta && meta.emphasize_time_for_date_time) {
    modConfigBeforeFormatting.rowHeight = 36;
  }

  const formattedData = formatData({
    config: modConfigBeforeFormatting,
    data: data.results ? data.results : data,
    meta,
    onSelect,
    addLinkColumn,
    checkedState,
    onNested,
    onGanttData,
    onInfo,
    lookups,
    viewOnly,
    useSimilarMatches,
    gantt,
  });

  return grid(
    data.results
      ? { ...formattedData, rows: { ...data, results: formattedData.rows } }
      : formattedData,
    api,
    modConfigBeforeFormatting,
    noDataMessage,
    height,
    lookups,
    className,
    updateConfig,
    isNotDeletable,
  );
};

export default UseBktGrid;
